<template>
  <div class="space-y-4">
    <div v-if="!editing" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <div class="flex items-center justify-between gap-4 px-4 py-3 border-b border-gray-200">
        <div>
          <div class="text-sm font-semibold text-gray-900">Dataset catalog</div>
          <div class="text-xs text-gray-600 mt-1">Saved input tables composed from one or more joined DataAttributes.</div>
        </div>
        <div class="flex items-center gap-2">
          <span class="text-xs text-gray-600">{{ datasets.length }} datasets</span>
          <button type="button" class="btn-primary" @click="newDataset">New dataset</button>
        </div>
      </div>
      <div class="px-4 py-3 bg-gray-10 border-b border-gray-200">
        <label for="dataset-search" class="sr-only">Search datasets</label>
        <input
          id="dataset-search"
          v-model="search"
          type="search"
          placeholder="Search by name or description"
          class="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-blue-dark focus:ring-offset-4"
        />
      </div>
      <LoadingIndicator v-if="loading" message="Loading datasets..." />
      <div v-else-if="filteredDatasets.length" class="max-h-[32rem] overflow-y-auto">
        <table class="w-full table-fixed border-collapse">
          <colgroup>
            <col class="w-[28%]" />
            <col class="w-[46%]" />
            <col class="w-[13%]" />
            <col class="w-[13%]" />
          </colgroup>
          <thead class="sticky top-0 z-10 bg-gray-25">
            <tr class="border-b border-gray-200">
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-600">Name</th>
              <th class="px-4 py-2 text-left text-xs font-semibold text-gray-600">Description</th>
              <th class="px-4 py-2 text-right text-xs font-semibold text-gray-600">Attributes</th>
              <th class="px-4 py-2 text-right text-xs font-semibold text-gray-600">Fields</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="dataset in filteredDatasets"
              :key="dataset.id"
              role="button"
              tabindex="0"
              class="border-b border-gray-50 last:border-b-0 cursor-pointer hover:bg-gray-25 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-dark align-top"
              @click="editDataset(dataset)"
              @keydown.enter="editDataset(dataset)"
              @keydown.space.prevent="editDataset(dataset)"
            >
              <td class="sensitive px-4 py-2 text-xs font-semibold text-gray-900 whitespace-normal break-words">{{ dataset.name }}</td>
              <td class="sensitive px-4 py-2 text-xs text-gray-600 whitespace-normal break-words" data-demo-blur>{{ dataset.description || 'No description provided.' }}</td>
              <td class="px-4 py-2 text-xs text-right text-gray-600 tabular-nums">{{ dataset.step_count }}</td>
              <td class="px-4 py-2 text-xs text-right text-gray-600 tabular-nums">{{ (dataset.output_columns || []).length }}</td>
            </tr>
          </tbody>
        </table>
      </div>
      <div v-else class="px-4 py-10 text-center">
        <div class="text-sm font-medium text-gray-900">No datasets found</div>
        <div class="text-xs text-gray-600 mt-2">Clear the search or create a dataset.</div>
      </div>
    </div>

    <template v-else>
      <div class="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div class="flex items-start gap-4">
          <div class="flex-1 min-w-0 grid grid-cols-[minmax(12rem,1fr)_minmax(18rem,2fr)] gap-4">
            <div>
              <label for="dataset-name" class="block text-xs font-semibold text-gray-700 mb-2">Name</label>
              <input id="dataset-name" v-model.trim="draft.name" class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
            <div>
              <label for="dataset-description" class="block text-xs font-semibold text-gray-700 mb-2">Description</label>
              <input id="dataset-description" v-model.trim="draft.description" class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            </div>
          </div>
          <div class="flex items-end gap-2 pt-6 shrink-0">
            <button type="button" class="btn-secondary" @click="closeEditor">Back</button>
            <button type="button" class="btn-secondary" :disabled="!canSave || previewing" @click="previewDataset">
              {{ previewing ? 'Previewing...' : 'Preview' }}
            </button>
            <button type="button" class="btn-primary" :disabled="!canSave || saving" @click="saveDataset">
              {{ saving ? 'Saving...' : 'Save dataset' }}
            </button>
            <button v-if="draft.id" type="button" class="btn-secondary" :disabled="profiling" @click="profileDataset">
              {{ profiling ? profilePhase : 'Profile' }}
            </button>
          </div>
        </div>
        <div v-if="error" class="text-sm text-red-dark">{{ error }}</div>
      </div>

      <StudioJoinBuilder
        v-model="draft.studio_config"
        v-model:excluded-columns="excludedColumns"
        :attributes="attributes"
        :columns-cache="columnsCache"
        :sql="combinedSql"
        :identifier-columns="draft.studio_config.primary_key_columns || []"
        @update:identifier-columns="setPrimaryKeys"
        @request-columns="fetchColumns"
        @refresh-sql="previewDataset"
        @attribute-saved="$emit('attribute-saved', $event)"
      />

      <div v-if="previewRows.length" class="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div class="px-4 py-3 border-b border-gray-200 text-sm font-semibold text-gray-900">Preview · first {{ previewRows.length }} rows</div>
        <div class="overflow-auto max-h-96">
          <table class="min-w-full text-xs">
            <thead class="sticky top-0 bg-gray-25">
              <tr><th v-for="column in previewColumns" :key="column" class="px-3 py-2 text-left font-semibold text-gray-600 border-b border-gray-200">{{ column }}</th></tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in previewRows" :key="index" class="border-b border-gray-50">
                <td v-for="column in previewColumns" :key="column" class="sensitive px-3 py-2 text-gray-700 whitespace-normal break-words">{{ row[column] }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="profileRun" class="bg-white border border-gray-200 rounded-lg p-4 space-y-4">
        <div class="flex items-center justify-between">
          <span class="text-sm font-semibold text-gray-900">Dataset profile</span>
          <span class="text-xs text-gray-600">{{ profileRun.status }}</span>
        </div>
        <pre v-if="['pending', 'running'].includes(profileRun.status)" class="text-xs bg-gray-900 text-gray-100 rounded p-3 overflow-auto max-h-40">{{ profileRun.console_output || 'Queued — waiting for a worker...' }}</pre>
        <div v-if="profileRun.status === 'failed'" class="text-sm text-red-dark">{{ profileRun.error_message }}</div>
        <div v-if="profileOutputs.length" class="space-y-4">
          <div v-for="(output, index) in profileOutputs" :key="index" class="border border-gray-200 rounded-lg p-4">
            <div class="text-xs font-semibold text-gray-700 mb-2">{{ output.name }}</div>
            <div v-if="output.kind === 'table'" class="overflow-auto max-h-80">
              <table class="min-w-full text-xs">
                <thead><tr><th v-for="column in output.columns" :key="column" class="px-2 py-1 text-left font-semibold text-gray-600 border-b border-gray-200">{{ column }}</th></tr></thead>
                <tbody><tr v-for="(row, rowIndex) in output.rows" :key="rowIndex"><td v-for="column in output.columns" :key="column" class="px-2 py-1 border-b border-gray-50">{{ row[column] }}</td></tr></tbody>
              </table>
            </div>
            <img v-else-if="output.kind === 'plot' && plotUrls[output.file]" :src="plotUrls[output.file]" :alt="output.name" class="max-w-full" />
            <p v-else class="text-sm text-gray-700">{{ output.detail }}</p>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import StudioJoinBuilder from '@/components/Admin/MetricStudio/builder/StudioJoinBuilder.vue';
import LoadingIndicator from '@/components/widgetcomponents/library/LoadingIndicator.vue';

export default {
  name: 'DatasetCatalog',
  components: { LoadingIndicator, StudioJoinBuilder },
  props: {
    datasets: { type: Array, default: () => [] },
    attributes: { type: Array, default: () => [] },
    loading: { type: Boolean, default: false },
  },
  emits: ['refresh', 'attribute-saved'],
  data() {
    return {
      search: '',
      editing: false,
      draft: this.emptyDraft(),
      excludedColumns: [],
      columnsCache: {},
      combinedSql: '-- Add a DataAttribute, then preview the dataset.',
      previewColumns: [],
      previewRows: [],
      previewing: false,
      saving: false,
      error: '',
      profileTemplate: '',
      profileRun: null,
      profileTimer: null,
      profiling: false,
      profilePhase: 'Profiling...',
      plotUrls: {},
    };
  },
  computed: {
    filteredDatasets() {
      const query = this.search.trim().toLowerCase();
      return this.datasets.filter((dataset) => !query
        || dataset.name.toLowerCase().includes(query)
        || (dataset.description || '').toLowerCase().includes(query));
    },
    canSave() {
      return Boolean(this.draft.name && (this.draft.studio_config.steps || []).length
        && (this.draft.studio_config.primary_key_columns || []).length);
    },
    allColumns() {
      const seen = new Set();
      const columns = [];
      for (const step of this.draft.studio_config.steps || []) {
        for (const column of this.columnsCache[step.da_id] || []) {
          if (!seen.has(column)) {
            seen.add(column);
            columns.push(column);
          }
        }
      }
      return columns;
    },
    selectedColumns() {
      const excluded = new Set(this.excludedColumns);
      return this.allColumns.filter((column) => !excluded.has(column));
    },
    profileOutputs() {
      return this.profileRun?.results?.outputs || [];
    },
  },
  beforeUnmount() {
    if (this.profileTimer) clearInterval(this.profileTimer);
    Object.values(this.plotUrls).forEach((url) => URL.revokeObjectURL(url));
  },
  methods: {
    emptyDraft() {
      return {
        id: null,
        name: '',
        description: '',
        output_columns: [],
        studio_config: { steps: [], primary_key_columns: [], output_columns: [] },
      };
    },
    async newDataset() {
      this.draft = this.emptyDraft();
      this.excludedColumns = [];
      this.previewRows = [];
      this.previewColumns = [];
      this.combinedSql = '-- Add a DataAttribute, then preview the dataset.';
      this.error = '';
      this.editing = true;
    },
    async editDataset(dataset) {
      const response = await axios.get(`api/impact_analytics/datasets/${dataset.id}/`);
      this.draft = JSON.parse(JSON.stringify(response.data));
      this.draft.studio_config = this.draft.studio_config || { steps: [], primary_key_columns: [] };
      await Promise.all((this.draft.studio_config.steps || []).map((step) => this.fetchColumns(step.da_id)));
      const selected = new Set(this.draft.studio_config.output_columns || this.draft.output_columns || []);
      this.excludedColumns = selected.size ? this.allColumns.filter((column) => !selected.has(column)) : [];
      this.previewRows = [];
      this.previewColumns = [];
      this.error = '';
      this.editing = true;
      await this.previewDataset();
    },
    closeEditor() {
      this.editing = false;
      this.profileRun = null;
      if (this.profileTimer) clearInterval(this.profileTimer);
    },
    setPrimaryKeys(columns) {
      this.draft.studio_config = {
        ...this.draft.studio_config,
        primary_key_columns: [...columns],
      };
    },
    async fetchColumns(daId) {
      if (!daId || this.columnsCache[daId]) return;
      try {
        const response = await axios.get(`/api/metrics/da-columns/${daId}/`);
        this.columnsCache = { ...this.columnsCache, [daId]: response.data.columns || [] };
      } catch {
        this.columnsCache = { ...this.columnsCache, [daId]: [] };
      }
    },
    payload() {
      return {
        name: this.draft.name,
        description: this.draft.description,
        studio_config: {
          ...this.draft.studio_config,
          output_columns: [...this.selectedColumns],
        },
      };
    },
    async previewDataset() {
      if (!(this.draft.studio_config.steps || []).length) return;
      this.previewing = true;
      this.error = '';
      try {
        const response = await axios.post('api/impact_analytics/datasets/preview-config/', {
          studio_config: this.payload().studio_config,
        });
        this.combinedSql = response.data.sql;
        this.previewColumns = response.data.columns || [];
        this.previewRows = response.data.rows || [];
      } catch (error) {
        this.error = error.response?.data?.error || error.message;
        this.combinedSql = `-- ${this.error}`;
      } finally {
        this.previewing = false;
      }
    },
    async saveDataset() {
      if (!this.canSave) return false;
      this.saving = true;
      this.error = '';
      try {
        const url = this.draft.id
          ? `api/impact_analytics/datasets/${this.draft.id}/`
          : 'api/impact_analytics/datasets/';
        const response = this.draft.id
          ? await axios.put(url, this.payload())
          : await axios.post(url, this.payload());
        this.draft = response.data;
        await Promise.all((this.draft.studio_config.steps || []).map((step) => this.fetchColumns(step.da_id)));
        this.$emit('refresh');
        return true;
      } catch (error) {
        const detail = error.response?.data;
        this.error = detail?.studio_config || detail?.name || detail?.error || detail?.detail || 'Could not save the dataset.';
        return false;
      } finally {
        this.saving = false;
      }
    },
    slugify(name) {
      let slug = (name || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
      if (/^\d/.test(slug)) slug = `x${slug}`;
      return slug;
    },
    renderProfileScript() {
      const columns = new Set((this.draft.output_columns || []).map((column) => column.toLowerCase()));
      const bindings = {
        id_col: (this.draft.studio_config.primary_key_columns || [])[0]?.toLowerCase() || '',
        age_col: columns.has('age') ? 'age' : '',
        gender_col: columns.has('gender') ? 'gender' : '',
        risk_col: columns.has('risk_score') ? 'risk_score' : '',
        cost_col: columns.has('prior_cost') ? 'prior_cost' : '',
        group_by: '',
      };
      let script = this.profileTemplate.replace(/ks_frame\("[^"]*"\)/, `ks_dataset("${this.slugify(this.draft.name)}")`);
      for (const [name, column] of Object.entries(bindings)) {
        script = script.replace(new RegExp(`(${name}\\s*=\\s*)"[^"]*"`), `$1"${column}"`);
      }
      return script;
    },
    async profileDataset() {
      if (!this.draft.id && !(await this.saveDataset())) return;
      this.profiling = true;
      this.error = '';
      try {
        if (!this.profileTemplate) {
          const templates = await axios.get('api/impact_analytics/study-templates/');
          this.profileTemplate = templates.data.find((template) => template.key === 'population_profile')?.script || '';
        }
        const name = `Profile Dataset: ${this.draft.name}`;
        const studies = await axios.get('api/impact_analytics/studies/', { params: { kind: 'profile' } });
        const existing = studies.data.find((study) => study.name === name);
        const payload = {
          name,
          script: this.renderProfileScript(),
          script_language: 'r',
          template_key: 'population_profile',
        };
        const study = existing
          ? await axios.put(`api/impact_analytics/studies/${existing.id}/`, payload)
          : await axios.post('api/impact_analytics/studies/', payload);
        const run = await axios.post(`api/impact_analytics/studies/${study.data.id}/run/`, {});
        this.profileRun = run.data;
        this.pollProfile(run.data.id);
      } catch (error) {
        this.error = error.response?.data?.error || error.message;
        this.profiling = false;
      }
    },
    pollProfile(runId) {
      if (this.profileTimer) clearInterval(this.profileTimer);
      this.profileTimer = setInterval(async () => {
        const response = await axios.get(`api/impact_analytics/study-runs/${runId}/`);
        this.profileRun = response.data;
        if (['completed', 'failed'].includes(response.data.status)) {
          clearInterval(this.profileTimer);
          this.profileTimer = null;
          this.profiling = false;
          await this.fetchPlots();
        }
      }, 2500);
    },
    async fetchPlots() {
      for (const output of this.profileOutputs.filter((item) => item.kind === 'plot')) {
        const name = (output.file || '').replace(/^plots\//, '').replace(/\.png$/, '');
        const response = await axios.get(
          `api/impact_analytics/study-runs/${this.profileRun.id}/artifact/`,
          { params: { name }, responseType: 'blob' },
        );
        this.plotUrls[output.file] = URL.createObjectURL(response.data);
      }
    },
  },
};
</script>
